import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HEALTH_COLORS, HEALTH_LABELS, derivePhases } from "@/lib/constants";
import { money } from "@/components/BudgetView";
import ReportRoadmap from "@/components/ReportRoadmap";
import ReportFlowStrip from "@/components/ReportFlowStrip";
import orbitIcon from "@/assets/orbit-icon.png";
import { Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sevOf = (s) => (s >= 15 ? { l: "Critical", c: "#EF4444" } : s >= 10 ? { l: "High", c: "#F97316" } : s >= 5 ? { l: "Moderate", c: "#F59E0B" } : { l: "Low", c: "#10B981" });

export default function PublicStatusReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [rmMode, setRmMode] = useState("roadmap");
  const [rmZoom, setRmZoom] = useState(100);

  useEffect(() => {
    axios.get(`${API}/public/status-report/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load this report"));
  }, [token]);

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-secondary/30 px-6 text-center">
      <img src={orbitIcon} alt="Project Management Orchestrator" className="h-10 w-auto mb-2" />
      <p className="text-lg font-semibold" data-testid="public-report-error">{err}</p>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const rep = data.report;
  const ov = rep.overall;
  const openActions = rep.actions.filter((a) => a.bucket !== "completed");
  const openRisks = [...rep.risks].sort((a, b) => (b.score || 0) - (a.score || 0));

  return (
    <div className="min-h-screen bg-secondary/20 py-10 px-4" data-testid="public-status-report">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-6"><img src={orbitIcon} alt="Project Management Orchestrator" className="h-8 w-auto" /><span className="font-display text-lg font-extrabold tracking-tight">Project Management Orchestrator</span><span className="ml-auto text-[11px] text-muted-foreground">Read-only · shared report</span></div>
        <div className="bg-white border border-border rounded-xl p-8 space-y-8">
          <div className="border-b border-border pb-4">
            <h1 className="font-display text-3xl font-extrabold tracking-tight">{data.title}</h1>
            <p className="text-sm text-muted-foreground mt-1">{ov.projects} project{ov.projects === 1 ? "" : "s"} · Generated {data.generated_at?.slice(0, 10)}</p>
          </div>

          <Section title="Health Overview">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <Metric label="Projects" value={ov.projects} />
              <Metric label="Overall progress" value={`${ov.avg_percent}%`} color="#0044FF" />
              <Metric label="Total tasks" value={ov.tasks} />
              <Metric label="Overdue" value={ov.overdue} color={ov.overdue ? "#EF4444" : undefined} />
            </div>
            <div className="flex flex-wrap gap-4">
              {["blue", "green", "yellow", "red", "gray"].filter((k) => ov.health_mix[k]).map((k) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-sm"><span className="h-3 w-3 rounded-full" style={{ background: HEALTH_COLORS[k] }} /> <b>{ov.health_mix[k]}</b> {HEALTH_LABELS[k]}</span>
              ))}
            </div>
          </Section>

          {rep.projects.length > 1 && (
            <Section title="Projects">
              <ul className="space-y-2">
                {[...rep.projects].sort((a, b) => a.avg_percent - b.avg_percent).map((p) => (
                  <li key={p.id} className="flex items-center gap-3 border-b border-border/50 pb-2">
                    <span className="h-3 w-3 rounded-full shrink-0" style={{ background: HEALTH_COLORS[p.health] }} />
                    <span className="text-sm font-medium w-48 truncate">{p.name}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${p.avg_percent}%` }} /></div>
                    <span className="text-sm font-semibold w-10 text-right">{p.avg_percent}%</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {data.roadmap && (
            <Section title="Roadmap & Key Dates">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="flex rounded-lg border border-border overflow-hidden text-xs font-semibold">
                  <button className={`px-3 py-1.5 transition-colors ${rmMode === "roadmap" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/60"}`} onClick={() => setRmMode("roadmap")} data-testid="public-rm-mode-roadmap">Summarized roadmap</button>
                  <button className={`px-3 py-1.5 transition-colors ${rmMode === "tasks" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/60"}`} onClick={() => setRmMode("tasks")} data-testid="public-rm-mode-tasks">Visual plan (tasks)</button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Timescale</span>
                  <input type="range" min="100" max="300" step="10" value={rmZoom} onChange={(e) => setRmZoom(Number(e.target.value))} className="w-28 accent-primary" data-testid="public-rm-zoom" />
                  <span className="text-xs tabular-nums text-muted-foreground w-10">{rmZoom === 100 ? "Fit" : `${rmZoom}%`}</span>
                </div>
              </div>
              <ReportRoadmap project={data.roadmap.project} tasks={data.roadmap.tasks} mode={rmMode} zoom={rmZoom} />
            </Section>
          )}

          {data.roadmap && (
            <Section title="IO Flow — Input → Tasks → Output">
              <ReportFlowStrip phases={data.roadmap.project.phases || []} tasks={data.roadmap.tasks} phaseColors={data.roadmap.project.phase_colors || {}} progressStyle={data.roadmap.project?.progress_style} />
            </Section>
          )}

          <Section title="Progress by Phase">
            <div className="space-y-2">
              {[...rep.phases].sort((a, b) => {
                if (!data.roadmap) return b.n - a.n;
                const order = derivePhases(data.roadmap.project, data.roadmap.tasks);
                const idx = (p) => { const i = order.indexOf(p); return i === -1 ? order.length : i; };
                return idx(a.phase) - idx(b.phase);
              }).map((r) => (
                <div key={r.phase} className="flex items-center gap-3">
                  <span className="text-sm w-40 truncate">{r.phase}</span>
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${r.pct}%` }} /></div>
                  <span className="text-sm font-semibold w-10 text-right">{r.pct}%</span>
                </div>
              ))}
            </div>
          </Section>

          {openRisks.length > 0 && (
            <Section title="Top Risks">
              <ul className="space-y-1.5">
                {openRisks.slice(0, 10).map((r, i) => {
                  const sv = sevOf(r.score || 0);
                  return (
                    <li key={i} className="flex items-center justify-between text-sm border-b border-border/50 pb-1.5">
                      <span className="truncate">{r.title} <span className="text-muted-foreground">— {r.project}</span></span>
                      <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded shrink-0" style={{ background: sv.c }}>{sv.l} ({r.score})</span>
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {openActions.length > 0 && (
            <Section title="Open Action Items">
              <ul className="space-y-1.5">
                {openActions.slice(0, 12).map((a, i) => (
                  <li key={i} className="flex items-center justify-between text-sm border-b border-border/50 pb-1.5">
                    <span className="truncate">{a.title} <span className="text-muted-foreground">— {a.project} · {a.owner}</span></span>
                    <span className="text-muted-foreground shrink-0">{a.due_date || "—"}{a.bucket === "late" ? " · late" : ""}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {rep.budget && (
            <Section title="Budget Snapshot">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Metric label="Budget" value={rep.budget.budget != null ? money(rep.budget.budget, rep.budget.currency) : "—"} />
                <Metric label="Planned cost" value={money(rep.budget.planned_cost, rep.budget.currency)} />
                <Metric label="Variance" value={rep.budget.budget_variance == null ? "—" : money(rep.budget.budget_variance, rep.budget.currency)} color={rep.budget.budget_variance < 0 ? "#EF4444" : "#10B981"} />
                <Metric label="Used" value={rep.budget.percent_used == null ? "—" : `${rep.budget.percent_used}%`} />
              </div>
            </Section>
          )}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6 flex items-center justify-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Powered by Project Management Orchestrator — Plan · Manage · Orchestrate</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="font-display text-lg font-bold tracking-tight mb-3 pb-1.5 border-b-2 border-primary/20">{title}</h3>
      {children}
    </section>
  );
}
function Metric({ label, value, color }) {
  return (
    <div className="border border-border rounded-md p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-display text-xl font-extrabold mt-1" style={color ? { color } : {}}>{value}</p>
    </div>
  );
}
